import React,{useEffect,useRef,useState} from 'react';
import API from '../api';
import CampaignCalendarPanel from './CampaignCalendarPanel';
import CampaignSettlementPanel from './CampaignSettlementPanel';
import CampaignLocalLayoutPanel from './CampaignLocalLayoutPanel';
import CampaignBuildingDefinitionsPanel from './CampaignBuildingDefinitionsPanel';
import CampaignLandSlotsPanel from './CampaignLandSlotsPanel';
import CampaignInitialRoadsPanel from './CampaignInitialRoadsPanel';
import CampaignInitialBuildingsPanel from './CampaignInitialBuildingsPanel';
import CampaignCivicRulesPanel from './CampaignCivicRulesPanel';
import CampaignInitialPropertyPanel from './CampaignInitialPropertyPanel';
import CampaignInitialSiteRightsPanel from './CampaignInitialSiteRightsPanel';
import CampaignInitialHousingPanel from './CampaignInitialHousingPanel';
import CampaignEnvironmentsPanel from './CampaignEnvironmentsPanel';
import CampaignWeatherPanel from './CampaignWeatherPanel';
import CampaignWaterPanel from './CampaignWaterPanel';
import CampaignEcologyPanel from './CampaignEcologyPanel';
import CampaignNpcSeedsPanel from './CampaignNpcSeedsPanel';
import CampaignGeographyPanel from './CampaignGeographyPanel';
import './CampaignCreationPanel.css';

const BASE='/v1/veridian/campaigns';
const action=()=>window.crypto.randomUUID();
function seed(){const words=new Uint32Array(2);window.crypto.getRandomValues(words);return ((window.BigInt(words[0])<<32n)|window.BigInt(words[1])).toString();}
const message=error=>error?.response?.data?.message||error?.response?.data?.error||'The request could not be completed. Try again.';

export default function CampaignCreationPanel({api=API,revision=0}){
 const [context,setContext]=useState(null),[campaigns,setCampaigns]=useState([]),[selected,setSelected]=useState({});
 const [title,setTitle]=useState(''),[busy,setBusy]=useState(false),[error,setError]=useState(''),[created,setCreated]=useState(null);
 const pending=useRef(null),reloadSequence=useRef(0);
 async function reload(){
  const sequence=++reloadSequence.current;
  const [a,b]=await Promise.all([api.get(BASE+'/context'),api.get(BASE)]);
  if(sequence!==reloadSequence.current)return;
  setContext(a.data);setCampaigns(b.data.campaigns);
 }
 useEffect(()=>{reload().catch(error=>setError(message(error)));return()=>{reloadSequence.current++;};},[revision]); // eslint-disable-line react-hooks/exhaustive-deps
 function edit(id,value){pending.current=null;setError('');setSelected(previous=>({...previous,[id]:value}));}
 async function create(){
  setError('');setBusy(true);
  try{
   if(!pending.current){
    const candidates=context.character_candidates.filter(row=>selected[row.draft_id]?.checked);
    if(candidates.length<1||candidates.length>4)throw Error('Choose one to four characters.');
    if(!title.trim())throw Error('Enter a campaign name.');
    const roster=candidates.map(row=>({row,name:row.name||selected[row.draft_id]?.name?.trim(),
     pcId:row.pc_id||action(),key:action(),sealId:null}));
    if(roster.some(row=>!row.name))throw Error('Enter a name for each character.');
    pending.current={roster,payload:{action_key:action(),campaign_id:action(),name:title.trim(),
     expected_account_version:context.account_version,pins:context.pins,seed:seed(),offline_policy:'paused',seal_ids:[]}};
   }
   const transaction=pending.current;
   for(const item of transaction.roster){
    if(!item.sealId){const reply=await api.post(BASE+'/seal',{draft_id:item.row.draft_id,expected_version:item.row.version,
     action_key:item.key,pc_id:item.pcId,name:item.name,pins:transaction.payload.pins});item.sealId=reply.data.seal_id;}
   }
   transaction.payload.seal_ids=transaction.roster.map(row=>row.sealId);
   const result=await api.post(BASE,transaction.payload);setCreated(result.data);pending.current=null;
   setSelected({});setTitle('');
   await reload().catch(()=>setError('Your campaign was saved. Reload this page to refresh saved campaigns.'));
  }catch(error){
   setError(error.response?message(error):error.message||message(error));
   // A network timeout retains exact keys, IDs, seed, names and successful
   // seals. A definite validation response allows corrected input.
   if(error.response?.status>=400&&error.response?.status<500){pending.current=null;await reload().catch(()=>{});}
  }finally{setBusy(false);}
 }
 return <section className="vd-build__panel" aria-label="Persistent campaign creation">
  <h2>Create a persistent campaign</h2><p>Choose one to four saved characters and give them names. Creation preserves their validated abilities and choices.</p>
  {error&&<p role="alert">{error}</p>}
  {!context&&<p>Loading saved characters…</p>}
  {context&&<><label>Campaign name<input aria-label="Campaign name" value={title} maxLength={120} disabled={busy}
   onChange={event=>{pending.current=null;setTitle(event.target.value);}} /></label>
   <fieldset disabled={busy}><legend>Characters</legend>
    {!context.character_candidates.length&&<p>Save a character draft above to add it here.</p>}
    {context.character_candidates.map(row=><div key={row.draft_id}>
     <label><input type="checkbox" checked={selected[row.draft_id]?.checked||false}
      disabled={!selected[row.draft_id]?.checked&&Object.values(selected).filter(value=>value.checked).length>=4}
      onChange={event=>edit(row.draft_id,{...selected[row.draft_id],checked:event.target.checked})}/>{row.race_ref} · {row.class_ref}</label>
     {selected[row.draft_id]?.checked&&<label>Character name<input aria-label={'Character name '+row.draft_id} maxLength={120}
      value={row.name||selected[row.draft_id]?.name||''} readOnly={row.identity_sealed}
      onChange={event=>edit(row.draft_id,{...selected[row.draft_id],name:event.target.value})}/></label>}
    </div>)}
   </fieldset><button type="button" disabled={busy} onClick={create}>{busy?'Saving campaign…':'Create campaign'}</button></>}
  {created&&<p role="status">{created.name} is saved with {created.pc_refs.length} character{created.pc_refs.length===1?'':'s'} and is prepared for world setup.</p>}
  {campaigns.length>0&&<><h3>Saved campaigns</h3><ul>{campaigns.map(row=><li key={row.campaign_id}>{row.name} · {row.status==='DRAFT'?'Prepared':row.status}
   <CampaignCalendarPanel campaign={row} api={api}/><CampaignSettlementPanel campaign={row} api={api}/><CampaignGeographyPanel campaign={row} api={api}/><CampaignEnvironmentsPanel campaign={row} api={api}/><CampaignWeatherPanel campaign={row} api={api}/><CampaignWaterPanel campaign={row} api={api}/><CampaignEcologyPanel campaign={row} api={api}/><CampaignNpcSeedsPanel campaign={row} api={api}/><CampaignLocalLayoutPanel campaign={row} api={api}/><CampaignBuildingDefinitionsPanel campaign={row} api={api}/><CampaignLandSlotsPanel campaign={row} api={api}/><CampaignInitialRoadsPanel campaign={row} api={api}/><CampaignInitialBuildingsPanel campaign={row} api={api}/><CampaignCivicRulesPanel campaign={row} api={api}/><CampaignInitialPropertyPanel campaign={row} api={api}/><CampaignInitialSiteRightsPanel campaign={row} api={api}/><CampaignInitialHousingPanel campaign={row} api={api}/></li>)}</ul></>}
 </section>;
}
